import { useMemo, useState } from 'react';
import { Button, Card, Space, Tag, Form, Input, Checkbox, App } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../../lib/api';
import { ResponsiveTable, type ResponsiveColumnType } from '../../components/common/responsive';
import { getSubscriptionStatusColor, getSubscriptionStatusLabel } from '../../components/company/subscriptionStateHelpers';
import { withDevModalFilePath } from '../../components/common';

const { Modal } = withDevModalFilePath('client/src/pages/partner/PartnerCompaniesPage.tsx');

const PARTNER_SCOPE_LABELS: Record<string, string> = {
  'companies:read': '会社情報の閲覧',
  'locations:read': 'エリアの閲覧',
  'locations:write': 'エリアの更新',
  'equipments:read': '設備の閲覧',
  'equipments:write': '設備の更新',
  'documents:read': '書類の閲覧',
  'documents:write': '書類の更新',
  'files:upload': '書類のアップロード',
  'files:share': 'ファイル共有',
  'reports:read': '報告書の閲覧',
  'reports:write': '報告書の作成・提出',
  'users:manage': '外部ユーザー管理',
};

const getScopeResources = (scopes: unknown): string[] => {
  if (!scopes || typeof scopes !== 'object') {
    return [];
  }
  const record = scopes as Record<string, unknown>;
  if (!Array.isArray(record.resources)) {
    return [];
  }
  return record.resources.filter((entry): entry is string => typeof entry === 'string');
};

const PARTNER_SCOPE_OPTIONS = [
  { label: '会社情報の閲覧', value: 'companies:read' },
  { label: 'エリアの閲覧', value: 'locations:read' },
  { label: 'エリアの更新', value: 'locations:write' },
  { label: '設備の閲覧', value: 'equipments:read' },
  { label: '設備の更新', value: 'equipments:write' },
  { label: '書類の閲覧', value: 'documents:read' },
  { label: '書類の更新', value: 'documents:write' },
  { label: '書類のアップロード', value: 'files:upload' },
  { label: 'ファイル共有', value: 'files:share' },
  { label: '報告書の閲覧', value: 'reports:read' },
  { label: '報告書の作成・提出', value: 'reports:write' },
  { label: '外部ユーザー管理', value: 'users:manage' },
];

interface PartnerAccessRow {
  id: string;
  companyId: string;
  companyName: string;
  status: string;
  accessRole?: string | null;
  assignedManagerUserId?: string | null;
  assignedManagerName?: string | null;
  scopes?: Record<string, unknown> | null;
  company?: {
    id: string;
    name: string;
    isActive: boolean;
    subscriptionPlan?: string | null;
    subscriptionStatus?: string | null;
  } | null;
}

interface ConnectionForm {
  companyId: string;
  scopes: string[];
}

export function PartnerCompaniesPage() {
  const { message } = App.useApp();
  const navigate = useNavigate();
  const utils = trpc.useUtils();
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [form] = Form.useForm<ConnectionForm>();

  const { data, isLoading } = trpc.partnerPortal.listAccessibleCompanies.useQuery({
    status: 'active',
    page: 1,
    limit: 100,
  });
  const { data: partnerUsersData } = trpc.partnerAdmin.partnerListUsers.useQuery(
    { page: 1, limit: 100 },
    { retry: false }
  );

  const requestMutation = trpc.partnerPortal.requestConnection.useMutation({
    onSuccess: () => {
      message.success('接続申請を送信しました');
      setRequestModalOpen(false);
      form.resetFields();
      utils.partnerPortal.listRequests.invalidate();
    },
    onError: (error) => {
      message.error(error.message || '接続申請に失敗しました');
    },
  });

  const partnerUserMap = useMemo(() => {
    const map = new Map<string, { name: string; email: string; isActive: boolean }>();
    (partnerUsersData?.users ?? []).forEach((user) => {
      map.set(user.id, { name: user.name, email: user.email, isActive: user.isActive });
    });
    return map;
  }, [partnerUsersData?.users]);

  const rows = useMemo<PartnerAccessRow[]>(
    () =>
      (data?.access ?? [])
        .filter((row) => Boolean(row?.id))
        .map((row) => {
          const assignedManager = row.assignedManagerUserId
            ? partnerUserMap.get(row.assignedManagerUserId)
            : undefined;
          return {
            ...row,
            companyName: row.company?.name ?? row.companyId,
            assignedManagerName: assignedManager
              ? `${assignedManager.name}${assignedManager.isActive ? '' : '（招待中）'}`
              : null,
          };
        }) as PartnerAccessRow[],
    [data?.access, partnerUserMap]
  );

  const getSubscriptionPlanLabel = (plan?: string | null) => {
    if (!plan) {
      return '不明';
    }

    const planLabels: Record<string, string> = {
      free: 'フリー',
      starter: 'スターター',
      standard: 'スタンダード',
      professional: 'プロフェッショナル',
      enterprise: 'エンタープライズ',
      enterprise_premium: 'エンタープライズ・プレミアム',
      premium: 'プレミアム',
    };

    return planLabels[plan] ?? plan.toUpperCase();
  };

  const columns: ResponsiveColumnType<PartnerAccessRow>[] = [
    {
      title: '会社名',
      dataIndex: ['company', 'name'],
      key: 'company',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.companyName}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.companyId}</div>
        </div>
      ),
      mobileRole: 'title',
      mobilePriority: 1,
    },
    {
      title: 'ロール',
      dataIndex: 'accessRole',
      key: 'accessRole',
      width: 140,
      render: (role) => role || '-',
      mobileRole: 'tag',
      mobilePriority: 2,
    },
    {
      title: '担当者',
      dataIndex: 'assignedManagerName',
      key: 'assignedManagerName',
      width: 160,
      render: (name, record) => {
        if (name) {
          return name;
        }
        return record.assignedManagerUserId ? '未取得' : '-';
      },
      mobileRole: 'description',
      mobilePriority: 3,
    },
    {
      title: '契約プラン',
      dataIndex: ['company', 'subscriptionPlan'],
      key: 'subscriptionPlan',
      width: 160,
      render: (_, record) => (
        <Tag>{getSubscriptionPlanLabel(record.company?.subscriptionPlan)}</Tag>
      ),
      mobileRole: 'tag',
      mobilePriority: 4,
    },
    {
      title: '契約状態',
      dataIndex: ['company', 'subscriptionStatus'],
      key: 'subscriptionStatus',
      width: 140,
      render: (_, record) => {
        const status = record.company?.subscriptionStatus ?? null;
        return (
          <Tag color={getSubscriptionStatusColor(status)}>
            {getSubscriptionStatusLabel(status)}
          </Tag>
        );
      },
      mobileRole: 'tag',
      mobilePriority: 5,
    },
    {
      title: '権限',
      dataIndex: 'scopes',
      key: 'scopes',
      render: (scopes) => {
        const resources = getScopeResources(scopes);
        return resources.length > 0
          ? resources.map((value: string) => PARTNER_SCOPE_LABELS[value] ?? value).join(' / ')
          : '未設定';
      },
      mobileRole: 'description',
      mobilePriority: 6,
    },
    {
      title: 'ステータス',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status) => (
        <Tag color={status === 'active' ? 'green' : 'default'}>
          {status}
        </Tag>
      ),
      mobileRole: 'tag',
      mobilePriority: 7,
    },
    {
      title: '操作',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Button type="link" onClick={() => navigate(`/partner/companies/${record.companyId}`)}>
          詳細
        </Button>
      ),
      mobileRole: 'hidden',
    },
  ];

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const requestedScopes = values.scopes.length > 0
        ? { resources: values.scopes }
        : undefined;

      requestMutation.mutate({
        companyId: values.companyId,
        requestedScopes,
      });
    });
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>担当会社</h1>
        <Space>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/partner/companies/new')}
          >
            新規会社登録
          </Button>
          <Button onClick={() => setRequestModalOpen(true)}>
            接続申請
          </Button>
        </Space>
      </div>

      <Card>
        <ResponsiveTable
          dataSource={rows}
          columns={columns}
          loading={isLoading}
          rowKey="companyId"
          pagination={false}
          mobileCardConfig={{
            titleField: 'companyName',
            onClick: (record) => navigate(`/partner/companies/${record.companyId}`),
          }}
        />
      </Card>

      <Modal
        title="接続申請"
        open={requestModalOpen}
        onCancel={() => {
          setRequestModalOpen(false);
          form.resetFields();
        }}
        onOk={handleSubmit}
        confirmLoading={requestMutation.isPending}
        okText="申請"
        cancelText="キャンセル"
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="companyId"
            label="会社ID"
            rules={[{ required: true, message: '会社IDを入力してください' }]}
          >
            <Input placeholder="会社ID (UUID)" />
          </Form.Item>
          <Form.Item name="scopes" label="希望権限（任意）" initialValue={[]}>
            <Checkbox.Group
              options={PARTNER_SCOPE_OPTIONS}
            />
          </Form.Item>
        </Form>
      </Modal>
    </Space>
  );
}
